import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { useInventoryText } from "@/lib/inventory/i18n";
import { loadZXing } from "./zxing";

type Detector = {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]>;
};
type DetectorConstructor = {
  new (options: { formats: string[] }): Detector;
  getSupportedFormats: () => Promise<string[]>;
};
async function nativeDetector(): Promise<Detector | null> {
  const Factory = (
    globalThis as typeof globalThis & { BarcodeDetector?: DetectorConstructor }
  ).BarcodeDetector;
  try {
    if (!Factory) return null;
    const supported = await Factory.getSupportedFormats();
    if (!supported.includes("code_128")) return null;
    return new Factory({
      formats: ["code_128", "ean_13", "ean_8", "qr_code"].filter((format) =>
        supported.includes(format),
      ),
    });
  } catch {
    return null;
  }
}
export function BarcodeCamera({
  onCode,
  disabled = false,
}: {
  onCode: (code: string) => void;
  disabled?: boolean;
}) {
  const t = useInventoryText();
  const [active, setActive] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [error, setError] = useState<
    "cameraSecure" | "cameraFailed" | "decodeFailed" | null
  >(null);
  const video = useRef<HTMLVideoElement>(null);
  const generation = useRef(0);
  const imageGeneration = useRef(0);
  const stream = useRef<MediaStream | null>(null);
  const controls = useRef<{ stop: () => void } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blobUrls = useRef(new Set<string>());
  const callback = useRef(onCode);
  callback.current = onCode;
  function cleanup() {
    generation.current += 1;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    controls.current?.stop();
    controls.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
    if (video.current) video.current.srcObject = null;
  }
  function stop() {
    cleanup();
    setActive(false);
  }
  useEffect(
    () => () => {
      cleanup();
      imageGeneration.current += 1;
      blobUrls.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrls.current.clear();
    },
    [],
  );
  useEffect(() => {
    if (disabled) {
      stop();
      imageGeneration.current += 1;
      setImageBusy(false);
    }
  }, [disabled]);
  async function start() {
    if (active || disabled) return;
    setError(null);
    if (!globalThis.isSecureContext) {
      setError("cameraSecure");
      return;
    }
    cleanup();
    const current = generation.current;
    setActive(true);
    try {
      const ownStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" } },
      });
      if (current !== generation.current) {
        ownStream.getTracks().forEach((track) => track.stop());
        return;
      }
      stream.current = ownStream;
      const detector = await nativeDetector();
      if (current !== generation.current || !video.current) return;
      function accept(value: string) {
        if (current !== generation.current) return;
        stop();
        callback.current(value);
      }
      if (detector) {
        video.current.srcObject = ownStream;
        await video.current.play();
        const tick = async () => {
          if (current !== generation.current || !video.current) return;
          try {
            if (video.current.readyState >= 2) {
              const results = await detector.detect(video.current);
              if (results[0]) {
                accept(results[0].rawValue);
                return;
              }
            }
          } catch {
            if (current === generation.current) {
              stop();
              setError("cameraFailed");
            }
            return;
          }
          if (current === generation.current)
            timer.current = setTimeout(() => void tick(), 200);
        };
        void tick();
      } else {
        const reader = await loadZXing();
        if (current !== generation.current || !video.current) return;
        const ownControls = await reader.decodeFromStream(
          ownStream,
          video.current,
          (result: { getText: () => string } | undefined) => {
            if (result) accept(result.getText());
          },
        );
        if (current !== generation.current) ownControls.stop();
        else controls.current = ownControls;
      }
    } catch {
      if (current === generation.current) {
        stop();
        setError("cameraFailed");
      }
    }
  }
  async function decodeFile(file?: File) {
    if (!file || disabled || imageBusy) return;
    stop();
    setError(null);
    setImageBusy(true);
    const current = ++imageGeneration.current;
    let url: string | undefined;
    try {
      if (!file.type.startsWith("image/") || file.size > 20 * 1024 * 1024)
        throw new Error("invalid image");
      url = URL.createObjectURL(file);
      blobUrls.current.add(url);
      const reader = await loadZXing();
      const result = await reader.decodeFromImageUrl(url);
      if (current === imageGeneration.current)
        callback.current(result.getText());
    } catch {
      if (current === imageGeneration.current) setError("decodeFailed");
    } finally {
      if (url && blobUrls.current.delete(url)) URL.revokeObjectURL(url);
      if (current === imageGeneration.current) setImageBusy(false);
    }
  }
  return (
    <div className="space-y-3 rounded border border-border p-3">
      <p className="text-sm">{t.cameraHelp}</p>
      <video
        ref={video}
        muted
        playsInline
        className={active ? "max-h-72 w-full rounded bg-black" : "hidden"}
      />
      <div className="flex flex-wrap gap-3">
        <Button
          variant="secondary"
          disabled={disabled || imageBusy}
          onClick={() => (active ? stop() : void start())}
        >
          {active ? t.stopCamera : t.camera}
        </Button>
        <label className="flex flex-col gap-1 text-sm">
          {t.localImage}
          <input
            type="file"
            accept="image/*"
            disabled={disabled || active || imageBusy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              void decodeFile(file);
            }}
          />
        </label>
      </div>
      {imageBusy && <p role="status">{t.loading}</p>}
      {error && <p role="alert">{t[error]}</p>}
    </div>
  );
}
